/**
 * X Ads — numbers, money, dates and labels.
 *
 * MONEY: X counts money in micros (1 unit = 1,000,000). Budgets and bids travel as micros;
 * the conversion happens only here, at the edges. Reporting `spend` is already in currency
 * units (the backend divides billed_charge_local_micro).
 */
export const MICROS = 1_000_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Currency units typed by the person → whole micros; null when empty / not a number. */
export function toMicros(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * MICROS);
}

export const fromMicros = (micros: number | null | undefined): number | null => (micros === null || micros === undefined ? null : micros / MICROS);

export function money(amount: number | null | undefined, currency?: string | null): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  if (currency && /^[A-Z]{3}$/.test(currency)) {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount); }
    catch { /* unknown currency code — plain number below */ }
  }
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export const microsMoney = (micros: number | null | undefined, currency?: string | null): string => money(fromMicros(micros), currency);

export const num = (v: number | null | undefined): string => (v === null || v === undefined || !Number.isFinite(v) ? '—' : v.toLocaleString());

/** "WEBSITE_CLICKS" → "Website clicks". */
export function label(value: string | null | undefined): string {
  if (!value) return '—';
  const s = value.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const FUNDING_TYPES: Record<string, string> = {
  CREDIT_CARD: 'Card on file', INSERTION_ORDER: 'Insertion order', AGENCY_CREDIT_LINE: 'Agency credit line', CREDIT_LINE: 'Credit line',
};

/**
 * A funding instrument's name. X keeps the text it wrote before any card was added
 * ("(no payment method has been set up yet)") even once the account has one, so while X
 * says the instrument can pay, that text — or none — gives way to the instrument's type.
 */
export function fundingName(f: { id: string; description?: string; type?: string; ableToFund?: boolean }): string {
  const d = f.description?.trim();
  if (d && (!/^\(.*no payment method.*\)$/i.test(d) || f.ableToFund === false)) return d;
  return (f.type && (FUNDING_TYPES[f.type] ?? label(f.type))) || (d ? 'Payment method' : f.id);
}

/** "AGE_18_TO_34" → "18–34", "AGE_OVER_25" → "25+". */
export function ageLabel(bucket: string): string {
  const range = /^AGE_(\d+)_TO_(\d+)$/.exec(bucket);
  if (range) return `${range[1]}–${range[2]}`;
  const over = /^AGE_OVER_(\d+)$/.exec(bucket);
  return over ? `${over[1]}+` : label(bucket);
}

/** "10 Oct 2026, 2:30 pm"; X's own text when it cannot be read. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

/** "10 Oct 2026". */
export function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A `yyyy-MM-dd` day as "10 Oct" — read from the string itself, so the day never shifts with the browser's zone. */
export function dayLabel(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1] ?? m[2]}` : day;
}

/** Today's date (`yyyy-MM-dd`) in the ads account's time zone (X's day buckets start there). */
export function todayIn(timeZone?: string | null, now = new Date()): string {
  const fmt = (tz: string) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = (name: string) => parts.find((p) => p.type === name)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  };
  try { return fmt(timeZone || 'UTC'); } catch { return fmt('UTC'); }
}

/** `yyyy-MM-dd` plus / minus whole days. */
export function addDays(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`, both included. */
export const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

/** The last `days` days (today included) in the account's time zone. */
export function lastDays(days: number, timeZone?: string | null): { from: string; to: string } {
  const to = todayIn(timeZone);
  return { from: addDays(to, -(days - 1)), to };
}

/** Today on the person's own clock as a Date at 00:00 — the earliest day a schedule picker offers. */
export function localDayStart(now = new Date()): Date { return new Date(now.getFullYear(), now.getMonth(), now.getDate()); }

/** A local "yyyy-MM-ddTHH:mm" value (DateTimePicker, the person's own clock) → ISO UTC for the API. */
export function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** A CSV cell; text that a spreadsheet would run as a formula is neutralised. */
export function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = typeof value === 'string' && /^[=+@\-\t\r\n]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function downloadCsv(fileName: string, rows: Array<Array<string | number | null | undefined>>): void {
  const body = '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Tone of an X entity status for a pill. */
export function statusTone(status?: string | null): 'success' | 'warning' | 'danger' | 'neutral' {
  switch ((status ?? '').toUpperCase()) {
    case 'ACTIVE': case 'RUNNING': case 'ACCEPTED': return 'success';
    case 'PAUSED': case 'DRAFT': case 'SCHEDULED': case 'UNDER_REVIEW': case 'PENDING': return 'warning';
    case 'REJECTED': case 'EXPIRED': case 'DELETED': return 'danger';
    default: return 'neutral';
  }
}
